import React, { useEffect, useRef, useState } from "react";
import { gallerySections } from "./consts";

const Gallery = () => {
  const [activeYear, setActiveYear] = useState(2025); // Start with 2025 as default
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Modal States
  const [isOpen, setIsOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentImagesArray, setCurrentImagesArray] = useState<string[]>([]);
  const [showYearList, setShowYearList] = useState(false);
  const galleryStartRef = useRef<HTMLDivElement | null>(null);

  const galleryEndRef = useRef<HTMLDivElement | null>(null);

  const [galleryStarted, setGalleryStarted] = useState(false);
  const [galleryEnded, setGalleryEnded] = useState(false);


  useEffect(() => {
    const handleScroll = () => {
      if (!galleryStartRef.current || !galleryEndRef.current) return;

      const startRect = galleryStartRef.current.getBoundingClientRect();
      const endRect = galleryEndRef.current.getBoundingClientRect();

      /**
       * Show floating year list when:
       * - Gallery start is above viewport bottom
       * - Gallery end is below viewport top
       */
      const isInsideGallery =
        startRect.top <= window.innerHeight &&
        endRect.top >= 0;

      setShowYearList(isInsideGallery);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // run once on mount

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);





  // Years to show in floating list
  const yearList = [2026, 2025, 2024, 2023, 2022, 2021];

  // Check if a year has any content
  const yearHasContent = (year: number) => {
    return gallerySections.some(section => section.year === year);
  };

  // Open modal
  const openModal = (imgArr: string[], index: number) => {
    setCurrentImagesArray(imgArr);
    setCurrentIndex(index);
    setCurrentImage(imgArr[index]);
    setIsOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsOpen(false);
  };

  // Next image
  const nextImage = () => {
    const newIndex = (currentIndex + 1) % currentImagesArray.length;
    setCurrentIndex(newIndex);
    setCurrentImage(currentImagesArray[newIndex]);
  };

  // Prev image
  const prevImage = () => {
    const newIndex =
      (currentIndex - 1 + currentImagesArray.length) %
      currentImagesArray.length;
    setCurrentIndex(newIndex);
    setCurrentImage(currentImagesArray[newIndex]);
  };

  // Close modal on ESC key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [currentIndex, currentImagesArray]);

  // IntersectionObserver to track active year
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const year = entry.target.getAttribute("data-year");
            if (year) setActiveYear(parseInt(year));
          }
        });
      },
      { threshold: 0.3 } // Reduced threshold for better detection
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Scroll to first section of a year when clicking a year
  const scrollToYear = (year: number) => {
    const index = gallerySections.findIndex((s) => s.year === year);
    if (index >= 0) {
      const section = sectionRefs.current[index];
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
        setActiveYear(year);
      }
    }
  };

  const formatOrdinalTitle = (title: string): string => {
    return title.replace(
      /(\d+)(st|nd|rd|th)/gi,
      (_: string, number: string, suffix: string) =>
        `${number}<sup class="text-xs align-super ml-0.5">${suffix}</sup>`
    );
  };


  return (
    <div className="relative w-full px-6 py-10 md:py-20">
      {/* Floating Year List - Only shows 2025-2021 */}
      <div ref={galleryStartRef} />
      {showYearList && (
        <div
          className="
      fixed left-4 top-1/2 -translate-y-1/2
      bg-white shadow-lg
      rounded-xl p-4
      flex flex-col gap-4
      z-50
    "
        >
          {yearList.map((year) => {
            const hasContent = yearHasContent(year);
            const isActive = activeYear === year;

            return (
              <div
                key={year}
                onClick={() => hasContent && scrollToYear(year)}
                className={`
            cursor-pointer text-lg transition
            ${hasContent
                    ? isActive
                      ? "text-gray-700 font-bold"
                      : "text-gray-500 hover:text-gray-700"
                    : "text-gray-300 cursor-not-allowed"
                  }
          `}
              >
                {year}
              </div>
            );
          })}
        </div>
      )}



      {/* Content Sections */}
      {gallerySections.map((section, idx) => (
        <div
          key={idx}
          data-year={section.year}
          ref={(el) => {
            sectionRefs.current[idx] = el;
          }}
          className="mb-20"
        >
          <h2
            className="text-xl md:text-2xl font-bold text-gray-900 mb-5"
            dangerouslySetInnerHTML={{
              __html: formatOrdinalTitle(section.title),
            }}
          />


          {/* {section.subtitle && (
            <p className="text-gray-600 text-sm mb-4">{section.subtitle}</p>
          )} */}

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
            {section.images?.map((img, index) => (
              <div
                key={index}
                className="group rounded-xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1.5 hover:border-[#ea4b20]/60 transition-all duration-500 border border-gray-200 cursor-pointer bg-white"
                onClick={() => openModal(section.images, index)}
              >
                <img
                  src={img}
                  alt="gallery"
                  className="w-full h-48 md:h-56 object-cover brightness-95 transition-all duration-500 group-hover:scale-108 group-hover:brightness-105"
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* MODAL */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[999]"
          onClick={closeModal}
        >
          <div
            className="relative max-w-4xl w-full px-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 cursor-pointer right-4 text-white text-3xl bg-black/40 hover:bg-black/60 rounded-full w-10 h-10 flex items-center justify-center"
              onClick={closeModal}
            >
              &times;
            </button>

            {/* Prev Button (Circular) */}
            <button
              onClick={prevImage}
              className="
                absolute left-3 top-1/2 -translate-y-1/2
                bg-white/20 hover:bg-white/30
                text-white text-3xl
                w-12 h-12 rounded-full
                flex items-center justify-center
                backdrop-blur-sm
                transition cursor-pointer
              "
            >
              ❮
            </button>

            {/* Image */}
            <img
              src={currentImage}
              className="max-h-[80vh] w-auto mx-auto rounded-lg shadow-lg"
            />

            {/* Next Button (Circular) */}
            <button
              onClick={nextImage}
              className="
                absolute right-3 top-1/2 -translate-y-1/2
                bg-white/20 hover:bg-white/30
                text-white text-3xl
                w-12 h-12 rounded-full
                flex items-center justify-center
                backdrop-blur-sm
                transition cursor-pointer
              "
            >
              ❯
            </button>
          </div>
        </div>
      )}
      <div ref={galleryEndRef} />
    </div>

  );
};

export default Gallery;